/**
 * `nts` template — net trade sales by fiscal month, as plain self-contained SVG `<rect>`s.
 *
 * Deliberately NOT `drawNTS` (`sku-velocity/field/lenses.ts`): that helper builds `.mbar` divs
 * styled by CSS scoped under `.skv` in `sku-velocity/proto.css`, which isn't in scope wherever
 * this template renders (the chat rail, the pinned dashboard). It reuses `ntsData`'s pure
 * aggregation — the exact monthly split the field's own Business widget reads — and draws its
 * own bars instead, so the numbers never drift even though the pixels are drawn from scratch.
 *
 * Bars are as wide as `MONTH_SLOTS` — the fiscal months the drop actually covers — same as the
 * field's own NTS widget, rather than one bar in eleven empty frames. A one-, two- or
 * three-month drop (the real UKDC snapshot ships a SINGLE fiscal month) is capped to a
 * reasonably-sized, centred bar rather than stretched into a full-width slab.
 */

import { useState } from "react";

import { ntsData } from "@/features/sku-velocity/field/lenses";
import type { NtsView } from "@/lib/api";
import { fmtMoneyShort, MONTH_SLOTS, type Sku } from "@/lib/data";
import { evalCohort } from "@/lib/specs/evaluate";

import { AXIS, AXIS_LABEL, spacedTicks } from "./axis";
import { ChartTooltip } from "./chart-tooltip";
import { HIGHLIGHT_PRIMARY, INK, NEUTRAL } from "./colors";
import { useChartSize } from "./use-chart-size";
import type { ViewChrome, ViewSize } from "./view-shell";

interface NtsChartProps {
  spec: NtsView;
  size: ViewSize;
  skus: readonly Sku[];
  chrome?: ViewChrome;
}

/** A visible cohort share never renders as a sliver too thin to see — the hover tooltip carries
 *  the exact percentage, this floor just keeps a nonzero month from reading as "no data". */
const MIN_FILL_PX = 3;

export function NtsChart({ spec, size, skus, chrome = "full" }: NtsChartProps) {
  const box = useChartSize();
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const sel = spec.cohort ? evalCohort(spec.cohort, skus) : null;
  const { mon: siteMon, val: siteVal } = ntsData(skus);
  const selMon = sel ? ntsData(sel).mon : null;

  // Drawn at the box's own pixel size — see `useChartSize`.
  const w = box.width;
  const h = box.height;
  const metrics = AXIS[size];
  const pad = { l: metrics.padL, r: metrics.padR, t: metrics.padT, b: metrics.padB };
  const fontSize = metrics.font;
  const plotW = Math.max(1, w - pad.l - pad.r);
  const plotH = Math.max(1, h - pad.t - pad.b);
  const gmax = Math.max(1, ...siteMon);

  const n = MONTH_SLOTS.length;
  const gap = Math.min(8, plotW / Math.max(1, n) / 3);
  // A one-, two- or three-month drop is centred at a capped width rather than stretched across
  // the whole frame — dividing the full plot width by `n` alone would make a single fiscal
  // month's one bar fill the entire box, which read as a broken/empty chart, not a bar chart.
  const maxBarFrac = n < 4 ? 0.26 : 1;
  const rawBarW = (plotW - gap * Math.max(0, n - 1)) / Math.max(1, n);
  const barW = Math.max(2, Math.min(rawBarW, plotW * maxBarFrac));
  const rowW = n * barW + gap * Math.max(0, n - 1);
  const startX = pad.l + Math.max(0, (plotW - rowW) / 2);
  const barX = (i: number) => startX + i * (barW + gap);

  // Thinned against the box, same as every other template's x axis — twelve month labels do
  // not all fit a narrow card. Positions are keyed on the bar's LOOP POSITION (`i`), not its
  // calendar month (`slot.mon`) — those only coincide for a drop that starts in January, and a
  // single non-January month previously thinned against the wrong x coordinate entirely.
  const indexed = MONTH_SLOTS.map((slot, i) => ({ slot, i }));
  const shownIdx = new Set(
    spacedTicks(indexed, (e) => barX(e.i) + barW / 2, fontSize * 2.4).map((e) => e.i),
  );

  const hovered = hoverIdx !== null ? MONTH_SLOTS[hoverIdx] : undefined;
  const hoveredSv = hoverIdx !== null ? (siteMon[hoverIdx] ?? 0) : 0;
  const hoveredFrac =
    hoverIdx !== null && selMon ? Math.min(1, (selMon[hoverIdx] ?? 0) / (hoveredSv || 1)) : null;
  // The hovered bar's own top, not a fixed row — same anchor rule every other template's
  // tooltip follows (positioned at the actual hovered element, not a shared axis coordinate).
  const hoveredTop = h - pad.b - Math.max(2, (hoveredSv / gmax) * plotH);

  return (
    <div className="relative flex h-full min-h-[90px] min-w-0 flex-col gap-1">
      <div ref={box.ref} className="relative min-h-0 min-w-0 flex-1">
        {w > 0 && h > 0 ? (
          <svg width={w} height={h}>
            <line
              x1={pad.l}
              x2={w - pad.r}
              y1={h - pad.b}
              y2={h - pad.b}
              stroke={INK}
              strokeWidth={1}
            />
            {MONTH_SLOTS.map((slot, i) => {
              const sv = siteMon[i] ?? 0;
              const barH = Math.max(2, (sv / gmax) * plotH);
              const x = barX(i);
              const yTop = h - pad.b - barH;
              const frac = selMon ? Math.min(1, (selMon[i] ?? 0) / (sv || 1)) : 0;
              // A nonzero share always shows SOME red, even when it would round to under a
              // pixel — a small cohort (e.g. "premium-slow") used to fill an invisible sliver
              // that read as no data at all. Capped to the bar's own height either way.
              const fillH = frac > 0 ? Math.min(barH, Math.max(frac * barH, MIN_FILL_PX)) : 0;
              return (
                <g key={slot.mon}>
                  <rect
                    x={x}
                    y={yTop}
                    width={barW}
                    height={barH}
                    fill={NEUTRAL}
                    fillOpacity={selMon ? 0.45 : 1}
                    rx={1}
                    tabIndex={0}
                    role="img"
                    aria-label={`${slot.label}: ${fmtMoneyShort(sv)}`}
                    className="cursor-default outline-none"
                    onMouseEnter={() => setHoverIdx(i)}
                    onMouseLeave={() => setHoverIdx((cur) => (cur === i ? null : cur))}
                    onFocus={() => setHoverIdx(i)}
                    onBlur={() => setHoverIdx((cur) => (cur === i ? null : cur))}
                  />
                  {fillH > 0 ? (
                    <rect
                      x={x}
                      y={yTop + barH - fillH}
                      width={barW}
                      height={fillH}
                      fill={HIGHLIGHT_PRIMARY}
                      rx={1}
                      className="pointer-events-none"
                    />
                  ) : null}
                  {shownIdx.has(i) ? (
                    <text
                      x={x + barW / 2}
                      y={h - pad.b + fontSize + 3}
                      fontSize={fontSize}
                      textAnchor="middle"
                      fill={AXIS_LABEL}
                    >
                      {slot.label}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </svg>
        ) : null}
      </div>
      {hovered ? (
        <ChartTooltip
          x={`${((barX(hoverIdx!) + barW / 2) / w) * 100}%`}
          y={`${(hoveredTop / h) * 100}%`}
        >
          <div className="font-semibold">{hovered.label}</div>
          <div>{fmtMoneyShort(hoveredSv)} NTS</div>
          {hoveredFrac !== null ? (
            <div>{(hoveredFrac * 100).toFixed(1)}% is the selection</div>
          ) : null}
        </ChartTooltip>
      ) : null}
      {chrome === "bare" ? null : (
        <p className="text-muted-foreground shrink-0 text-[11px]">
          {sel
            ? `Red = the selection’s share of each month’s NTS · site total ${fmtMoneyShort(siteVal)}`
            : `Site NTS by fiscal month · ${fmtMoneyShort(siteVal)} total`}
        </p>
      )}
    </div>
  );
}
